// Remembers the most recent patient import per schema so the patient-table
// viewer can highlight the rows that were just imported.
//
// Storage: one localStorage entry per schema, keyed
//   notesai.patientImport.recent.<schema>
// holding JSON of shape { at: <ms epoch>, keys: [ ehr_id, ... ] }. Every access
// is wrapped in try/catch so a disabled, full, or unavailable localStorage
// (private-browsing SecurityError, quota, SSR) degrades to a no-op / null rather
// than throwing into the caller.

const KEY_PREFIX = 'notesai.patientImport.recent.';

// Cap the stored key list so an enormous import can't blow the ~5MB per-origin
// localStorage budget. 5000 ehr_ids is plenty to highlight a single import.
const MAX_KEYS = 5000;

// Default expiry: a highlight older than a week is stale and stops showing.
const DEFAULT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function storageKey(schema) {
  return `${KEY_PREFIX}${schema}`;
}

// Return the active localStorage, or null if it can't be reached. Merely reading
// `window.localStorage` can throw in some privacy configurations, so guard it.
function getStore() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch (_) {
    /* localStorage access blocked — treat as unavailable */
  }
  return null;
}

/**
 * Store the ehr_id keys of the import that just ran for `schema`.
 *
 * @param {string} schema  The tenant schema the import targeted.
 * @param {string[]} keys  ehr_id strings just imported (coerced to strings,
 *                         capped at MAX_KEYS).
 * @param {{ now?: number }} [opts]  `now` is an injectable ms-epoch timestamp
 *                         (default Date.now()) stored as `at`.
 * @returns {boolean} true if written, false if localStorage was unavailable / threw.
 */
export function saveRecentImport(schema, keys, { now = Date.now() } = {}) {
  const store = getStore();
  if (!store || !schema) return false;
  try {
    const list = Array.isArray(keys) ? keys.map(String) : [];
    const capped = list.length > MAX_KEYS ? list.slice(0, MAX_KEYS) : list;
    store.setItem(storageKey(schema), JSON.stringify({ at: now, keys: capped }));
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Load the most recent import for `schema`.
 *
 * @param {string} schema
 * @param {{ maxAgeMs?: number, now?: number }} [opts]  `maxAgeMs` is the expiry
 *   window (default 7 days); an entry older than it loads as null. `now` is an
 *   injectable ms-epoch clock (default Date.now()) used only for the expiry check.
 * @returns {{ at: number, keys: Set<string> } | null} `keys` is a Set of ehr_id
 *   strings. Returns null when absent, unparseable, wrong-shaped, or expired.
 */
export function loadRecentImport(schema, { maxAgeMs = DEFAULT_MAX_AGE_MS, now = Date.now() } = {}) {
  const store = getStore();
  if (!store || !schema) return null;
  try {
    const raw = store.getItem(storageKey(schema));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.at !== 'number' || !Array.isArray(parsed.keys)) return null;
    if (maxAgeMs != null && now - parsed.at > maxAgeMs) return null;
    return { at: parsed.at, keys: new Set(parsed.keys.map(String)) };
  } catch (_) {
    return null;
  }
}

/**
 * Forget the remembered import for `schema`.
 * @returns {boolean} true if removed, false if localStorage was unavailable / threw.
 */
export function clearRecentImport(schema) {
  const store = getStore();
  if (!store || !schema) return false;
  try {
    store.removeItem(storageKey(schema));
    return true;
  } catch (_) {
    return false;
  }
}
